// Public report links (Oct 15, 2026). Ben: "when sharing reports, the link needs to be public and just bring them to a
// report"; links expire after 7 days. The report's Share button opens this sheet: the link (made the first time, then the
// same link until it expires), Copy link, the phone's own share menu, Post to Discord (the card carries the link), when it
// was shared and when it expires, and Stop sharing. The link opens PublicReport.tsx for anyone, with no sign-in.
// Oct 15 2026 (Ben: "let's also have an option to share to Valor Tools Guild Intel"): a scouting report also gets Add to
// Guild Intel — into a Valor Tools guild the player is in on this world (worker/intel.ts → Valor Tools src/vweb.js).
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { api, type ShareLink } from "@/api/client";
import type { ShareCard } from "@/game/share";
import { fmtClock, fmtDate } from "@/game/time";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { DiscordShareSheet, useDiscordStatus } from "./DiscordShare";
import { IconShareOut } from "./icons";
import { Sheet } from "./overlays";
import { Button, Empty, cx } from "./primitives";

export interface LinkInfo { url: string; at: number; exp: number }

// the links made on this device this session, by world and report (the Worker answers the same link anyway)
const made = new Map<string, LinkInfo>();
const listeners = new Set<() => void>();
const keyOf = (wid: number, rid: string) => `${wid}:${rid}`;
const emit = () => listeners.forEach((f) => f());
const subscribe = (f: () => void) => (listeners.add(f), () => void listeners.delete(f));

/** "Oct 22 · 14:31 UTC" */
export const shareStamp = (ms: number) => `${fmtDate(ms)} · ${fmtClock(ms)} UTC`;

export interface ReportLink {
  info: LinkInfo | null;
  busy: boolean;
  error: string | null;
  /** the link, made if it isn't yet; null (with `error` set) when it couldn't be */
  ensure: () => Promise<string | null>;
  /** Stop sharing: the link stops working for everyone */
  stop: () => Promise<boolean>;
}

const why = (r: ShareLink | { ok: boolean; error?: string }) =>
  r.error === "signed_out" ? "You've been signed out. Sign in again to share." : r.error || "Couldn't make the link. Try again in a moment.";

export function useReportLink(wid: number, rid: string): ReportLink {
  const key = keyOf(wid, rid);
  const info = useSyncExternalStore(subscribe, () => {
    const i = made.get(key);
    return i && i.exp > Date.now() ? i : null;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [key]);

  const ensure = useCallback(async () => {
    const have = made.get(key);
    if (have && have.exp > Date.now()) return have.url;
    setBusy(true);
    setError(null);
    const wname = useSession.getState().worldNames[String(wid)] ?? null;
    const r = await api.shares.create(wid, rid, wname);
    setBusy(false);
    if (!r.ok || !r.url) {
      setError(why(r));
      return null;
    }
    made.set(key, { url: r.url, at: r.at ?? Date.now(), exp: r.exp ?? Date.now() + 7 * 86400e3 });
    emit();
    return r.url;
  }, [key, wid, rid]);

  const stop = useCallback(async () => {
    setBusy(true);
    const r = await api.shares.stop(wid, rid);
    setBusy(false);
    if (!r.ok) {
      setError(r.error || "Couldn't stop sharing. Try again in a moment.");
      return false;
    }
    made.delete(key);
    emit();
    return true;
  }, [key, wid, rid]);

  return { info, busy, error, ensure, stop };
}

/** The report's Share button and its sheet. `intelRid`: a scouting report's id, which can also go to Guild Intel. */
export function ReportShareButton({ link, title, card, wid, intelRid }: { link: ReportLink; title: string; card: () => ShareCard; wid: number; intelRid?: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button kind="steel" size="md" onClick={() => setOpen(true)}>Share</Button>
      {open && <ReportShareSheet link={link} title={title} card={card} wid={wid} intelRid={intelRid} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ReportShareSheet({ link, title, card, wid, intelRid, onClose }: { link: ReportLink; title: string; card: () => ShareCard; wid: number; intelRid?: string | null; onClose: () => void }) {
  const [discord, setDiscord] = useState<ShareCard | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const { info, busy, error, ensure } = link;
  // the link is made when the sheet opens (the same one again within the 7 days)
  useEffect(() => {
    if (!info) void ensure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.url);
      usePrompts.getState().toast("Link copied.", "success");
    } catch {
      const el = document.getElementById("rshare-url") as HTMLInputElement | null;
      el?.select();
      usePrompts.getState().toast("Select the link and copy it.");
    }
  };
  const share = async () => {
    if (!info) return;
    try {
      await navigator.share({ title, text: title, url: info.url });
    } catch {
      /* cancelled */
    }
  };
  const stop = async () => {
    if (await link.stop()) {
      usePrompts.getState().toast("Stopped sharing. The link no longer works.", "success");
      onClose();
    }
  };

  if (discord) return <DiscordShareSheet wid={wid} card={discord} onClose={onClose} />;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  return (
    <Sheet open onClose={onClose} label="Share this report" title={<h3 className="eyebrow" style={{ margin: "0 0 12px" }}>Share this report</h3>}>
      {!info && busy && <Empty><span className="spin" /> Making the link…</Empty>}
      {!info && !busy && error && (
        <div className="rshare-missing">
          <div className="dshare-err" role="alert">{error}</div>
          <Button kind="gold" size="md" onClick={() => void ensure()}>Try again</Button>
        </div>
      )}
      {info && !confirmStop && (
        <>
          <div className="rshare-link">
            <input id="rshare-url" className="input" readOnly value={info.url} aria-label="Report link" onFocus={(e) => e.currentTarget.select()} />
            <span className="rshare-pill">Public</span>
          </div>
          <div className={cx("rshare-acts", canShare && "two")}>
            <Button kind="gold" size="md" onClick={() => void copy()}>Copy link</Button>
            {canShare && <Button kind="steel" size="md" onClick={() => void share()}>Share…</Button>}
          </div>
          <Button kind="crimson" size="md" className="rshare-dc" onClick={() => setDiscord({ ...card(), url: info.url })}><IconShareOut /> Post to Discord</Button>
          {intelRid && !intelOpen && <Button kind="steel" size="md" className="rshare-dc" onClick={() => setIntelOpen(true)}>Add to Guild Intel</Button>}
          {intelRid && intelOpen && <IntelPanel wid={wid} rid={intelRid} onDone={onClose} onCancel={() => setIntelOpen(false)} />}
          <p className="rshare-note">Anyone with the link can see this report as it is now, for 7 days. They can't see your other reports, cities or account.</p>
          <div className="rshare-row"><span>Shared {shareStamp(info.at)}</span><span className="rshare-exp">Expires {fmtDate(info.exp)}</span></div>
          <button type="button" className="rshare-row rshare-stop" onClick={() => setConfirmStop(true)}><span>Stop sharing</span><span className="rshare-sub">The link stops working</span></button>
          {error && <div className="dshare-err" role="alert">{error}</div>}
        </>
      )}
      {info && confirmStop && (
        <div className="rshare-confirm">
          <p>Stop sharing this report? Anyone who opens the link will see that it has expired.</p>
          <div className="c-grid2">
            <Button kind="steel" size="md" onClick={() => setConfirmStop(false)}>Keep sharing</Button>
            <Button kind="crimson" size="md" busy={busy} onClick={() => void stop()}>Stop sharing</Button>
          </div>
          {error && <div className="dshare-err" role="alert">{error}</div>}
        </div>
      )}
    </Sheet>
  );
}

const TOOLS = "https://valor-reforged-tools.com";

/** Add to Guild Intel: the guild (when the player has several on this world), then Add. */
function IntelPanel({ wid, rid, onDone, onCancel }: { wid: number; rid: string; onDone: () => void; onCancel: () => void }) {
  const st = useDiscordStatus(wid, true);
  const guilds = st.data?.intel ?? [];
  const [gid, setGid] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const g = guilds.find((x) => x.id === gid) ?? guilds[0] ?? null;
  const home = st.data?.home || TOOLS;
  const add = async () => {
    if (!g) return;
    setBusy(true);
    setErr(null);
    const r = await api.intel.add(wid, rid, g.id);
    setBusy(false);
    if (!r.ok) return setErr(r.error === "signed_out" ? "You've been signed out. Sign in again to share." : r.error || "Couldn't add it. Try again in a moment.");
    const name = `${r.tag ? `[${r.tag}] ` : ""}${r.guild || g.name}`;
    usePrompts.getState().toast(r.already ? `Already in ${name}'s Guild Intel.` : `Added to ${name}'s Guild Intel.`, "success");
    onDone();
  };
  return (
    <div className="rshare-intel" role="group" aria-label="Guild Intel">
      <div className="dshare-label">Add this scouting report to your guild's Intel on Valor Tools</div>
      {st.isLoading && <Empty><span className="spin" /> Checking your Valor Tools guilds…</Empty>}
      {st.data && !guilds.length && (
        <div className="dshare-missing">
          <p>{st.data.intelReason === "link"
            ? "Guild Intel is on Valor Tools. Link this game account there (Dashboard › Link my Valor account), then add reports here."
            : "You're not in a Valor Tools guild on this world. Join your guild on Valor Tools, then add reports here."}</p>
          <Button kind="gold" size="md" onClick={() => window.open(`${home}/app/?tab=${st.data!.intelReason === "link" ? "dashboard" : "guild"}`, "_blank", "noopener")}>Open Valor Tools</Button>
        </div>
      )}
      {g && (
        <>
          {guilds.length > 1 && (
            <div className="dshare-pick" role="radiogroup" aria-label="Guild">
              {guilds.map((x) => (
                <button key={x.id} type="button" role="radio" aria-checked={x.id === g.id} className={cx("dshare-chip", x.id === g.id && "on")} onClick={() => setGid(x.id)}>
                  {x.tag ? `[${x.tag}] ` : ""}{x.name}
                </button>
              ))}
            </div>
          )}
          {err && <div className="dshare-err" role="alert">{err}</div>}
          <div className="c-grid2" style={{ marginTop: 8 }}>
            <Button kind="steel" size="md" onClick={onCancel}>Cancel</Button>
            <Button kind="gold" size="md" busy={busy} onClick={() => void add()}>{guilds.length > 1 ? "Add" : `Add to ${g.tag ? `[${g.tag}]` : g.name}`}</Button>
          </div>
        </>
      )}
    </div>
  );
}
